import { ChevronDown } from "lucide-react";

export function Panel({ children, className = "", ...rest }) {
  return <div className={`bg-slate-900/70 border border-slate-800 rounded-md ${className}`} {...rest}>{children}</div>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function StyledSelect({ value, onChange, children, className = "", ...props }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        {...props}
        className={`appearance-none w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 pr-9 transition-colors ${className}`}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
    </div>
  );
}

export function TextField({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors ${className}`}
    />
  );
}

export function Label({ children }) {
  return <label className="font-mono text-xs uppercase tracking-wider text-slate-400">{children}</label>;
}

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  inactive: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  archived: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  suspended: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  draft: "bg-slate-500/10 text-slate-300 border-slate-600/40",
  submitted: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  sent_to_supplier: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  partially_received: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  fully_received: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  closed: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  cancelled: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  posted: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  reversed: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  planned: "bg-slate-500/10 text-slate-300 border-slate-600/40",
  released: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  in_progress: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  scheduled: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  out_for_delivery: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  dispatched: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  in_transit: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  available: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  maintenance: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  out_of_service: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  delivered: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  returned: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  failed: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  partial: "bg-amber-500/10 text-amber-400 border-amber-500/30",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider border ${STATUS_STYLES[status] || STATUS_STYLES.inactive}`}>
      {String(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export function Chip({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-800 text-slate-300 border-slate-700",
    orange: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono border ${tones[tone]}`}>{children}</span>;
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-md shadow-md transition-all active:scale-95 text-sm ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-slate-100 font-medium px-3 py-2 rounded-md transition-colors text-sm ${className}`}
    >
      {children}
    </button>
  );
}
